/**
 * URL format of the public site (apps/web, pontimed.app) — the one place
 * that knows it. Every link into www goes through `webPrefix` / `webPath`
 * (or `webLink` in ./links for absolute URLs); never concatenate
 * `/${locale}/${country}` by hand.
 *
 * Format: country first, then the language only when it differs from the
 * country's own language.
 *
 *   /ro                     Romania, Romanian
 *   /ro/find/…              Romania, Romanian
 *   /ro/en/provider/x       Romania, English
 *   /bg                     Bulgaria, Bulgarian
 *   /bg/ro/find             Bulgaria, Romanian
 *
 * `/{country}/{its own language}/…` is never canonical: apps/web's middleware
 * 301s it to the short form. Internally Next still renders the
 * `app/[locale]/[country]/…` tree; the middleware rewrites the public URL
 * onto it (`parseWebPath` below).
 *
 * ⚠️ `COUNTRY_DEFAULT_LOCALE` decides which language a URL without a language
 * segment means. Changing a country's entry there changes the meaning of its
 * already published URLs.
 */

import { COUNTRY_DEFAULT_LOCALE, SUPPORTED_COUNTRIES } from "./countries";

/** Every UI locale apps/web serves (8 mandatory + 7 secondary). Same list as
 *  `routing.locales` in apps/web/src/i18n/routing.ts, which imports it. */
export const WEB_LOCALES = [
  "ro",
  "en",
  "bg",
  "de",
  "fr",
  "hu",
  "it",
  "el",
  "hr",
  "sr",
  "cs",
  "es",
  "pl",
  "tr",
  "uk",
] as const;

export type WebLocale = (typeof WEB_LOCALES)[number];

export function isWebLocale(value: string): value is WebLocale {
  return (WEB_LOCALES as readonly string[]).includes(value);
}

/** The language a www URL without a language segment is in. */
export function countryDefaultLocale(country: string): string {
  return COUNTRY_DEFAULT_LOCALE[country.toLowerCase()] ?? "en";
}

/**
 * Path prefix of a country + language: "/ro" or "/ro/en".
 *
 * @example
 *   webPrefix("ro", "ro") → "/ro"
 *   webPrefix("ro", "en") → "/ro/en"
 *   webPrefix("BG", "bg") → "/bg"
 */
export function webPrefix(country: string, locale: string): string {
  const cc = country.toLowerCase();
  return locale === countryDefaultLocale(cc) ? `/${cc}` : `/${cc}/${locale}`;
}

/**
 * Root-relative www path. `path` may carry a query string or hash.
 *
 * @example
 *   webPath("ro", "ro", "/find")          → "/ro/find"
 *   webPath("ro", "en", "provider/x")     → "/ro/en/provider/x"
 *   webPath("bg", "bg")                   → "/bg"
 *   webPath("ro", "ro", "?openWaitlist=1") → "/ro?openWaitlist=1"
 */
export function webPath(country: string, locale: string, path = ""): string {
  const prefix = webPrefix(country, locale);
  const clean = path.replace(/^\/+/, "");
  if (!clean) return prefix;
  if (clean.startsWith("?") || clean.startsWith("#")) return `${prefix}${clean}`;
  return `${prefix}/${clean}`;
}

export interface ParsedWebPath {
  country: string;
  locale: string;
  /** Whether the language segment was present in the URL. */
  explicitLocale: boolean;
  /** Segments after the country (+ language), e.g. ["find", "cardiology"]. */
  rest: string[];
}

/**
 * Read country + language out of a www pathname in the public format.
 * Returns null when the first segment is not a supported country (old
 * `/{locale}/{country}` URLs, root handlers, unknown paths).
 *
 * The second segment counts as a language only when it is one of
 * WEB_LOCALES — no route under a country is a two-letter locale code, so
 * `/ro/find` and `/ro/en/find` never collide. Keep it that way.
 */
export function parseWebPath(pathname: string): ParsedWebPath | null {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0];
  if (!first || !SUPPORTED_COUNTRIES.includes(first)) return null;

  const second = segments[1];
  if (second && isWebLocale(second)) {
    return { country: first, locale: second, explicitLocale: true, rest: segments.slice(2) };
  }
  return {
    country: first,
    locale: countryDefaultLocale(first),
    explicitLocale: false,
    rest: segments.slice(1),
  };
}
